import { ReactNode } from "react";

import {
    DurationType,
    StrategyDuration,
    TokenListTag,
    UiTokenListTag,
    formatDurationWithTypeShorthand,
    isUiTokenListTag,
    TokenListMetadata
} from "@bridgesplit/abf-sdk";
import {
    BsMetaUtil,
    LendingVaultExpanded,
    LoopExpanded,
    MarketGuideMode,
    RoleView,
    serializeStrategyDuration
} from "@bridgesplit/abf-react";
import {
    AccountBalanceOutlined,
    AddBusinessOutlined,
    AppsOutlined,
    AutoAwesome,
    BusinessCenterOutlined,
    CurrencyBitcoinOutlined,
    ConfirmationNumberOutlined,
    CurrencyExchangeOutlined,
    DiamondOutlined,
    HowToVoteOutlined,
    LayersOutlined,
    LocalAtmOutlined,
    LockOutlined,
    OilBarrelOutlined,
    RocketLaunchOutlined,
    SensorsOutlined
} from "@mui/icons-material";
import {
    BORDER_RADIUS_ROUNDED,
    Button,
    ButtonProps,
    Icon,
    MEDIA,
    SolanaSvg,
    SxType,
    TextProps,
    TextSkeleton,
    ToggleButtons,
    TooltipText,
    TextVariant
} from "@bridgesplit/ui";
import { encodeQueryParams } from "@bridgesplit/react";
import { ALL_VALUE, AllValueType, isAll, tokenSymbolToUrlSlug } from "@bridgesplit/utils";
import { MARKET_SLUG, MARKETS_SLUG } from "app/constants";
import { AppDialog, AppParams, useAppDialog } from "app/utils";
import { useMediaQuery } from "@mui/material";

export function getMarketPath({
    token,
    collateralMint,
    term,
    view,
    navigateTo = "detail"
}: {
    view: RoleView | undefined;
    token: TokenListMetadata | { symbol: string } | undefined;
    collateralMint?: string;
    term?: { duration: number; durationType: DurationType };
    navigateTo?: "detail" | "markets";
}) {
    const slug = MARKET_SLUG;
    if (!token) return slug;

    const queryParams = encodeQueryParams({
        [AppParams.CollateralMint]:
            "assetMint" in token && collateralMint === token.assetMint ? undefined : collateralMint,
        [AppParams.Term]: term ? formatDurationWithTypeShorthand(term.duration, term.durationType) : undefined,
        [AppParams.BorrowLendRole]: view === undefined ? view : view.toLowerCase()
    });
    if (navigateTo === "markets") {
        return `${MARKETS_SLUG}${queryParams}`;
    }

    const symbol = "symbol" in token ? token.symbol : BsMetaUtil.getSymbol(token);
    return `${MARKET_SLUG}/${tokenSymbolToUrlSlug(symbol)}${queryParams}`;
}

/** Frontend-only filter: "all" shows every token; specific tags filter by token metadata. */
export const TOKEN_TAG_FILTER_ALL = ALL_VALUE;
export type TokenTagFilter = AllValueType | UiTokenListTag;

type TokenTagMeta = {
    icon: JSX.Element;
    label: string;
    shortLabel: string;
    tag: TokenTagFilter;
    sortPosition: number;
    hideInCategories?: boolean;
};
const TOKEN_TAG_METADATA: Record<TokenTagFilter, Omit<TokenTagMeta, "tag" | "sortPosition">> = {
    [TOKEN_TAG_FILTER_ALL]: { icon: <AppsOutlined />, label: "All", shortLabel: "All", hideInCategories: true },
    [TokenListTag.Featured]: {
        icon: <AutoAwesome />,
        label: "Featured",
        shortLabel: "Featured",
        hideInCategories: true
    },
    [TokenListTag.Native]: { icon: <SolanaSvg />, label: "Native", shortLabel: "Native" },
    [TokenListTag.Stables]: { icon: <LocalAtmOutlined />, label: "Stable coins", shortLabel: "Stables" },
    [TokenListTag.LST]: { icon: <LayersOutlined />, label: "Liquid staking", shortLabel: "LSTs" },
    [TokenListTag.Bridged]: { icon: <CurrencyBitcoinOutlined />, label: "Bridged", shortLabel: "Bridged" },
    [TokenListTag.Governance]: { icon: <HowToVoteOutlined />, label: "Governance", shortLabel: "Governance" },
    [TokenListTag.Staked]: { icon: <BusinessCenterOutlined />, label: "Staked", shortLabel: "Staked" },
    [TokenListTag.Memes]: { icon: <RocketLaunchOutlined />, label: "Meme coins", shortLabel: "Memes" },
    [TokenListTag.RWA]: { icon: <AccountBalanceOutlined />, label: "RWA", shortLabel: "RWA" },
    [TokenListTag.Utility]: { icon: <BusinessCenterOutlined />, label: "Utility", shortLabel: "Utility" },
    [TokenListTag.DeFi]: { icon: <LayersOutlined />, label: "DeFi", shortLabel: "DeFi" },
    [TokenListTag.Treasuries]: { icon: <AccountBalanceOutlined />, label: "Treasuries", shortLabel: "Treasuries" },
    [TokenListTag.LP]: { icon: <CurrencyExchangeOutlined />, label: "LP Tokens", shortLabel: "LPs" },
    [TokenListTag.Misc]: { icon: <LockOutlined />, label: "Miscellaneous", shortLabel: "Misc" },
    [TokenListTag.xStock]: { icon: <AddBusinessOutlined />, label: "Stocks", shortLabel: "Stocks" },
    [TokenListTag.Vault]: { icon: <LockOutlined />, label: "Vault", shortLabel: "Vault" },
    [TokenListTag.Lockbox]: { icon: <LockOutlined />, label: "Lockbox", shortLabel: "Lockbox" },
    [TokenListTag.LRT]: { icon: <LayersOutlined />, label: "LRT", shortLabel: "LRT" },
    [TokenListTag.Commodity]: { icon: <OilBarrelOutlined />, label: "Commodities", shortLabel: "Commodities" },
    [TokenListTag.PrivateCredit]: {
        icon: <AccountBalanceOutlined />,
        label: "Private credit",
        shortLabel: "Private credit"
    },
    [TokenListTag.Collectibles]: { icon: <DiamondOutlined />, label: "Collectibles", shortLabel: "Collectibles" },
    [TokenListTag.Tickets]: { icon: <ConfirmationNumberOutlined />, label: "Tickets", shortLabel: "Tickets" },
    [TokenListTag.DePIN]: { icon: <SensorsOutlined />, label: "DePIN", shortLabel: "DePIN" }
};

export function getTokenTagMetadata(tag: TokenListTag | TokenTagFilter | undefined): TokenTagMeta | undefined {
    if (!tag || !(isAll(tag) || isUiTokenListTag(tag))) return undefined;
    return {
        ...TOKEN_TAG_METADATA[tag],
        tag,
        sortPosition: Object.keys(TOKEN_TAG_METADATA).findIndex((t) => t === tag)
    };
}

export function OpenHowItWorks({
    mode,
    variant = "outlined",
    loopExpanded,
    vaultExpanded,
    textProps: textPropsProp,
    sx: sxProp
}: {
    mode: MarketGuideMode;
    variant?: ButtonProps["variant"];
    loopExpanded?: LoopExpanded;
    vaultExpanded?: LendingVaultExpanded;
    textProps?: Partial<TextProps>;
    sx?: SxType;
}) {
    const { open: openDialog } = useAppDialog();

    const handleClick = () =>
        openDialog(AppDialog.MarketGuide, {
            mode,
            isForced: false,
            loopExpanded: loopExpanded,
            vaultExpanded: vaultExpanded
        });

    const isMobile = useMediaQuery(MEDIA.SM.below);
    return (
        <Button
            variant={variant}
            height={32}
            onClick={handleClick}
            sx={{
                borderRadius: BORDER_RADIUS_ROUNDED,
                px: variant === "text" ? 1 : 2,
                py: 0.5,
                ...sxProp
            }}
            textProps={{
                svgColor: "caption",
                variant: "body2",
                color: "caption",
                ...textPropsProp
            }}
            width="max-content"
        >
            {isMobile ? null : "How it works"}
            <Icon type="help" />
        </Button>
    );
}

export function BorrowCapWarning({
    children,
    borrowCapWarning,
    variant = "body1"
}: {
    children: ReactNode;
    borrowCapWarning: string | undefined | null;
    variant?: TextVariant;
}) {
    return (
        <TooltipText
            variant={variant}
            icon={false}
            helpText={borrowCapWarning ?? undefined}
            svgColor="error"
            translate="no"
        >
            {!!borrowCapWarning && <Icon type="warning" />}
            {children}
        </TooltipText>
    );
}

export function SelectStrategyDuration({
    strategyDurations,
    strategyDuration,
    setStrategyDuration
}: {
    strategyDurations: StrategyDuration[] | undefined;
    strategyDuration: StrategyDuration | undefined;
    setStrategyDuration: (strategyDuration: StrategyDuration) => void;
}) {
    return (
        <ToggleButtons
            allowNull
            options={
                strategyDurations?.map((preset) => ({
                    value: serializeStrategyDuration(preset),
                    label: formatDurationWithTypeShorthand(preset.duration, preset.durationType)
                })) ?? Array(4).fill({ value: "", label: <TextSkeleton variant="body2" width="30px" /> })
            }
            value={strategyDuration ? serializeStrategyDuration(strategyDuration) : ""}
            setValue={(serializedStrategyDuration) => {
                const strategyDuration = strategyDurations?.find(
                    (d) => serializeStrategyDuration(d) === serializedStrategyDuration
                );
                if (strategyDuration) {
                    setStrategyDuration(strategyDuration);
                }
            }}
        />
    );
}
